import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useMatch } from '@common/matchSwitch';
import MatchProposalModals from '@common/matchSwitch/matchProposalModals';
import Tab from '@components/common/tab';
import Icon, { IconNameType } from '@components/common/assets';
import MatchList from '@components/match/matchTab/matchList';
import {
  MATCHING_STATUS_QUERY_KEY,
  MATCH_LIST_QUERY_KEY,
  useMatchList,
  useMatchingResponseStatus,
  useMatchingStatus,
  useReadMatchingPositionAll,
} from '@repository/match';
import EmptyMatchList from '@components/match/matchTab/matchList/EmptyMatchList';
import ResponseRateText from '@components/match/matchTab/ResponseRateText';
import { useNotificationActions } from '@stores/NotificationStore';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { MatchingStatusListTabType } from '@domain/rq/match/MatchingStatusListRq';
import { queryClient } from '@utils/queryClient';
import UrlUtil from '@utils/urlUtil';

import useUpdateEffect from '@utils/hooks/useUpdateEffect';
import classnames from 'classnames/bind';
import styles from './matchTab.module.scss';
const cx = classnames.bind(styles);

interface TitleProps {
  title: string;
  count: number;
  isNew?: boolean;
}

const Title = ({ title, count, isNew }: TitleProps) => {
  return (
    <div className={cx('titleContainer')}>
      <div className={cx('count', { isNew })}>{count}</div>
      <div className={cx('title')}>{title}</div>
    </div>
  );
};

interface SubTitleProps {
  title: string;
  content: string;
  iconName: IconNameType;
  className?: string;
}

const SubTitle = ({ title, content, iconName, className }: SubTitleProps) => {
  return (
    <section className={cx('subTitleWrap', className)}>
      <div className={cx('subTitleArea')}>
        <Icon name={iconName} width={32} height={32} />
        <h2 className={cx('title')}>{title}</h2>
      </div>
      <p className={cx('content')}>{content}</p>
    </section>
  );
};

const MatchTab = () => {
  const { setMatchingCurrentPage } = useNotificationActions();

  const [currentPage, setCurrentPage] = useState(0);
  const [tabType, setTabType] = useState<MatchingStatusListTabType>('RECOMMEND');
  const [isFirstTooltipVisible, setIsFirstTooltipVisible] = useState(true);
  const pageParams = { page: currentPage, size: 5, tabType };
  const { query } = useRouter();
  const { mutate: readMatchingPositionAllMutate } = useReadMatchingPositionAll(() => {
    // 읽음 처리 후 매칭 현황 list | status 쿼리 다시 호출
    queryClient.refetchQueries([MATCH_LIST_QUERY_KEY, pageParams]);
    queryClient.refetchQueries(MATCHING_STATUS_QUERY_KEY);
  });
  const activeTab = Array.isArray(query.tab) ? query.tab[0] : query.tab ?? 'RECOMMEND';
  if (activeTab) {
    // tab query 제거
    UrlUtil.removeQueryStringAndReplace('tab');
  }

  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;
  const { data: matchList } = useMatchList(true, pageParams);
  const { data: matchStatus } = useMatchingStatus(isLogin);
  const { data: responseStatus } = useMatchingResponseStatus();
  const { modalState, setModalState, matchConfigStatus, profilesTypeData, handleMatchingConfigYn } = useMatch();

  useUpdateEffect(() => {
    setMatchingCurrentPage(currentPage);
  }, [currentPage]);

  if (!matchList || !matchStatus || !responseStatus) return null;

  const { matchingList, firstRecommendOrProfileOpenMatchingSn } = matchList.pages[0].matchingList;
  const pageInfo = matchList.pages[0].pages;
  const { recommendCount, offerCount, profileOpenCount, newRecommendYn, newOfferYn, newProfileOpenYn } = matchStatus;
  const { responseRate } = responseStatus;

  const onTabChange = (tabIndex?: string) => {
    setCurrentPage(0);
    setIsFirstTooltipVisible(true);

    switch (tabIndex) {
      case 'RECOMMEND':
        setTabType(tabIndex);
        if (newRecommendYn) readMatchingPositionAllMutate(tabIndex);
        break;
      case 'PROFILE_OPEN':
        setTabType(tabIndex);
        if (newProfileOpenYn) readMatchingPositionAllMutate(tabIndex);
        break;
      case 'OFFER':
        setTabType(tabIndex);
        if (newOfferYn) readMatchingPositionAllMutate(tabIndex);
        break;
    }
  };

  const handleTooltipClose = () => {
    setIsFirstTooltipVisible(false);
  };

  return (
    <>
      <Tab className={cx('container')} active={activeTab} onTabChange={onTabChange}>
        <Tab.Item
          title={<Title title="추천" count={recommendCount} isNew={newRecommendYn} />}
          index="RECOMMEND"
          className={cx('item')}
          activeClassName={cx('active')}
          nonActiveClassName={cx('nonActive')}
        >
          <SubTitle
            title="추천된 포지션"
            content="잡다의 전문 큐레이터가 나의 프로필과 역량 정보를 확인하고, 최적의 포지션에 추천한 목록입니다."
            iconName="verifiedLight"
          />
          {recommendCount > 0 ? (
            <MatchList
              matchingList={matchingList}
              pageInfo={pageInfo}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageParams={pageParams}
              firstRecommendOrProfileOpenMatchingSn={firstRecommendOrProfileOpenMatchingSn}
              isFirstTooltipVisible={isFirstTooltipVisible}
              handleTooltipClose={handleTooltipClose}
            />
          ) : (
            <EmptyMatchList handleMatchingConfigYn={handleMatchingConfigYn} />
          )}
        </Tab.Item>
        <Tab.Item
          title={<Title title="열람" count={profileOpenCount} isNew={newProfileOpenYn} />}
          index="PROFILE_OPEN"
          className={cx('item')}
          activeClassName={cx('active')}
          nonActiveClassName={cx('nonActive')}
        >
          <SubTitle
            title="내 정보 열람 포지션"
            content="내 정보를 열람한 포지션 목록입니다. 관심 표현해 놓은 포지션이면 제안받을 가능성이 높아져요."
            iconName="folderLight"
          />
          {profileOpenCount > 0 ? (
            <MatchList
              matchingList={matchingList}
              pageInfo={pageInfo}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageParams={pageParams}
              firstRecommendOrProfileOpenMatchingSn={firstRecommendOrProfileOpenMatchingSn}
              isFirstTooltipVisible={isFirstTooltipVisible}
              handleTooltipClose={handleTooltipClose}
            />
          ) : (
            <EmptyMatchList handleMatchingConfigYn={handleMatchingConfigYn} />
          )}
        </Tab.Item>
        <Tab.Item
          title={<Title title="제안" count={offerCount} isNew={newOfferYn} />}
          index="OFFER"
          className={cx('item')}
          activeClassName={cx('active')}
          nonActiveClassName={cx('nonActive')}
        >
          <div className={cx('offerSubTitleWrap')}>
            <SubTitle
              title="제안 받은 포지션"
              content="인사담당자가 직접 보낸 제안 포지션 목록입니다. 응답률이 높을수록 더 좋은 제안받을 수 있어요."
              iconName="workLight"
              className={cx('offerSubTitle')}
            />
            <ResponseRateText responseRate={responseRate} className={cx('responseRateText')} />
          </div>

          {offerCount > 0 ? (
            <MatchList
              matchingList={matchingList}
              pageInfo={pageInfo}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageParams={pageParams}
              firstRecommendOrProfileOpenMatchingSn={firstRecommendOrProfileOpenMatchingSn}
              isFirstTooltipVisible={isFirstTooltipVisible}
              handleTooltipClose={handleTooltipClose}
            />
          ) : (
            <EmptyMatchList handleMatchingConfigYn={handleMatchingConfigYn} />
          )}
        </Tab.Item>
      </Tab>

      <MatchProposalModals
        modalState={modalState}
        setModalState={setModalState}
        matchConfigStatus={matchConfigStatus}
        profilesTypeData={profilesTypeData}
      />
    </>
  );
};

export default MatchTab;
